import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { getTier } from '../utils/tier';
import './RivalCard.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const SHOWN_KEY = 'rival_card_last_shown';
const SUPPRESS_DAYS = 3;
const SUPPRESS_MS = SUPPRESS_DAYS * 24 * 60 * 60 * 1000;

interface FriendEntry {
  user_id: string;
  nickname: string;
  rating: number;
  has_played_ranked?: boolean;
  rating_updated_at?: string | null;
}

export function RivalCard() {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [rival, setRival] = useState<FriendEntry | null>(null);
  const [myRating, setMyRating] = useState<number | null>(null);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    // Her denemede önce sıfırla — token/kullanıcı değişimi veya erken
    // dönüşlerden birinde eski kullanıcının kartı görünür kalmasın.
    setEligible(false);
    setRival(null);
    setMyRating(null);

    if (!token || !user?.id) return;
    const shownKey = `${SHOWN_KEY}_${user.id}`;
    const lastShown = parseInt(localStorage.getItem(shownKey) || '0', 10);
    if (Date.now() - lastShown < SUPPRESS_MS) return;

    let aborted = false;
    fetch(`${API}/stats/season/leaderboard/friends?limit=200`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (aborted || !data?.leaderboard) return;
        const entries: FriendEntry[] = data.leaderboard;
        const me = entries.find(e => e.user_id === user.id);
        if (!me) return;

        // Son 3 gün içinde ranked oynadıysa nudge'a gerek yok. rating_updated_at
        // hiç maç oynamamış kullanıcılarda da (rating senkronizasyonuyla) güncellenebiliyor,
        // bu yüzden has_played_ranked olmadan tek başına güvenilir değil.
        const recentlyPlayedRanked = !!me.has_played_ranked && !!me.rating_updated_at
          && Date.now() - new Date(me.rating_updated_at).getTime() < SUPPRESS_MS;
        if (recentlyPlayedRanked) return;

        const others = entries.filter(e => e.user_id !== user.id && e.has_played_ranked !== false);
        if (others.length === 0) return;
        const closest = others.reduce((a, b) =>
          Math.abs(b.rating - me.rating) < Math.abs(a.rating - me.rating) ? b : a
        );
        setMyRating(me.rating);
        setRival(closest);
        setEligible(true);
        localStorage.setItem(shownKey, String(Date.now()));
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [token, user?.id]);

  if (!token || !user?.id || !eligible || !rival || myRating === null) return null;
  const diff = rival.rating - myRating;
  const tier = getTier(rival.rating);
  const isAhead = diff > 0;
  const rc = t.rivalCard as any;
  const label = rc.label;
  const aheadTxt = rc.aheadTxt.replace('{n}', String(Math.abs(diff)));
  const behindTxt = rc.behindTxt.replace('{n}', String(Math.abs(diff)));
  const equalTxt = rc.equalTxt;
  const challengeTxt = rc.challengeTxt;

  return (
    <div className="rival-card" onClick={() => navigate('/challenge')}>
      <div className="rival-icon">🎯</div>
      <div className="rival-body">
        <div className="rival-label">{label}</div>
        <div className="rival-name">
          {rival.nickname}
          <span className="rival-tier" style={{ color: tier.color, borderColor: tier.color }}>
            {tier.icon} {rival.rating}
          </span>
        </div>
        <div className="rival-diff">
          {diff === 0 ? equalTxt : isAhead ? aheadTxt : behindTxt}
        </div>
      </div>
      <div className="rival-cta">{challengeTxt}</div>
    </div>
  );
}
